import { useParams } from "react-router-dom";
import EditMaintenanceReport from "./Modals/EditMaintenanceReport";
import React, { useEffect, useState } from "react";
import instance from "../../Interceptor/api_instance";
import { SpinnerOverlay } from "../../Components/LoadingAnimations/SpinnerOverlay";
import { convertToPST } from "../../Helper/FormatDatetimes";
import { useTranslation } from "react-i18next";
import NewInvoice from "./Modals/NewInvoice";
import InvoiceDetails from "./Modals/InvoiceDetails";
import { PageLayout } from "../../Components/PageLayout";
import {
  EditMaintenanceTask,
  NewMaintenanceTask,
} from "./Modals/MaintenanceTask";
import { TableCell } from "../../Components/Tables/Components/TableCell";
import { useWindowWidth } from "../../Hooks/useWindowWidth";
import { NewPartModal } from "./Modals/MaintenancePart";
import { CheckOutIcon, TrashIconCont } from "../../Media/Icons";
import DeleteMaintenanceReport from "./Modals/DeleteMaintenanceReport";
import {
  ActionConfirm,
  ActionResponse,
} from "../../Components/Modals/ActionResponses";
import { GetVehicleCondition } from "../../Components/Tables/Vehicles/GetVehicleCondition";
import NewDoc from "./Modals/NewDoc";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { useApiMutation } from "../../Hooks/useApiMutation";
import { useFetch } from "../../Hooks/useFetch";
import { Button } from "../shared";

function sliceName(name) {
  if (name.length <= 20) return name;
  return name.slice(0, 20) + "...";
}

const CompleteModal = ({ submit, close }) => {
  const { t } = useTranslation();
  return (
    <div className="page-mask page-mask-animation">
      <div className="success-message">
        <h2 className="global-h2 warning">{t("warnings.warning")}</h2>
        <h2 className="global-h4">
          {t("warnings.complete-maintenance-report")}
        </h2>
        <div className="full-cont">
          <button
            className="change-pwd-btn"
            onClick={() => {
              submit();
              close();
            }}
          >
            {t("buttons.submit")}
          </button>
          <button className="close-btn" onClick={close}>
            {t("buttons.close")}
          </button>
        </div>
      </div>
    </div>
  );
};

const MaintenanceReportView = () => {
  const { t } = useTranslation();
  const { order_number } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [newInvoiceOpen, setNewInvoiceOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [workOrderModal, setWorkOrderModal] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);

  const openInvoice = async (inv) => {
    try {
      setIsLoading(true);
      const res = await instance.get(`safety/get-invoice/${inv.id}`);
      if (res.status === 200) {
        setSelectedInvoice(res.data);
      }
    } catch (err) {
      console.error(err);
      setResponseModal({ status: "Error", message: err });
    } finally {
      setIsLoading(false);
    }
  };

  const [newTask, setNewTask] = useState(false);
  const [selectedTask, setSelectedTask] = useState(false);

  const [newPart, setNewPart] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null);

  const [responseModal, setResponseModal] = useState(null);

  const isComplete = reportData?.status === "closed";

  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const isMobile = useWindowWidth() <= 500;
  const [completeModal, setCompleteModal] = useState({
    display: false,
    complete: null,
  });

  const markComplete = async () => {
    try {
      setIsLoading(true);
      const response = await instance.patch(
        `safety/complete-maintenance-report/${order_number}`,
      );
      if (response.status === 200) {
        setReportData((prev) => ({
          ...prev,
          status: response.data.status,
          completed_at: response.data.completed_at,
        }));
        setError(null);
      }
    } catch (error) {
      console.log(error);
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const markDispatch = async () => {
    try {
      setIsLoading(true);
      const response = await instance.patch(
        `safety/dispatch-maintenance-report/${order_number}`,
      );
      if (response.status === 200) {
        setReportData((prev) => ({
          ...prev,
          status: response.data.status,
          completed_at: response.data.completed_at,
        }));
        setError(null);
      }
    } catch (error) {
      console.log(error);
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const undoComplete = async () => {
    try {
      setIsLoading(true);
      const response = await instance.patch(
        `safety/undo-maintenance-report/${order_number}`,
      );
      if (response.status === 200) {
        setReportData((prev) => ({
          ...prev,
          status: response.data.status,
          completed_at: response.data.completed_at,
        }));
        setError(null);
      }
    } catch (error) {
      console.log(error);
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePart = async () => {
    try {
      setIsLoading(true);
      const response = await instance.delete(
        `safety/remove-part-from-report/${selectedPart.id}`,
      );
      if (response.status === 200) {
        setReportData((prev) => ({
          ...prev,
          used_parts: prev.used_parts.filter(
            (part) => part.id !== selectedPart.id,
          ),
        }));
        setSelectedPart(null);
      }
    } catch (error) {
      setSelectedPart(null);
      console.log(error);
      setResponseModal({
        status: "Error",
        message: error,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchReportData = async () => {
      try {
        const response = await instance.get(
          `safety/maintenance-report/${order_number}`,
        );
        if (response.status === 200) {
          setReportData(response.data);
        }
      } catch (err) {
        console.error(err);
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchReportData();
  }, []);

  const {
    mutate: deleteWorkOrder,
    isLoading: isDeletingWorkOrder,
    error: deleteWorkOrderError,
  } = useApiMutation(async () => {
    const response = await instance.delete(
      `files/blobs/${selectedWorkOrder.id}`,
    );
    if (response.status === 200) {
      setReportData((prev) => ({
        ...prev,
        files: prev.files.filter((file) => file.id !== selectedWorkOrder.id),
      }));
      setSelectedWorkOrder(null);
    }
  });

  const { run: downloadFile } = useFetch(
    async (file) => {
      const response = await instance.get(`/files/blobs/${file.args[0].id}/`);
      const link = document.createElement("a");
      link.href = response.data.sas_url;
      link.download = response.data.blob_name;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    [],
    { enabled: false },
  );

  return (
    <PageLayout title={"Maintenance Report"}>
      {isDeleting && (
        <DeleteMaintenanceReport
          id={reportData.id}
          close={() => setIsDeleting(false)}
        />
      )}
      {responseModal && (
        <ActionResponse
          status={responseModal.status}
          message={responseModal.message}
          close={() => setResponseModal(null)}
        />
      )}
      {completeModal.display && (
        <CompleteModal
          close={() => setCompleteModal({ display: false, complete: null })}
          submit={completeModal.complete ? markComplete : undoComplete}
        />
      )}
      {error && <p className="error">{error}</p>}
      {newInvoiceOpen && (
        <NewInvoice
          maintenance_report_id={reportData.id}
          close={() => setNewInvoiceOpen(false)}
          onCreate={(payload) => {
            setResponseModal({
              status: "Success",
              message: "Invoice created (local)",
            });
            setReportData((prev) => ({
              ...prev,
              invoices: [...(prev.invoices || []), payload],
            }));
          }}
        />
      )}
      {selectedInvoice && (
        <InvoiceDetails
          invoice={selectedInvoice}
          close={() => setSelectedInvoice(null)}
          onUpdate={(payload, meta) => {
            // If delete occurred, remove invoice from local state
            if (meta && meta.deleted && meta.id) {
              setReportData((prev) => ({
                ...prev,
                invoices: prev.invoices.filter((inv) => inv.id !== meta.id),
              }));
              setResponseModal({
                status: "Success",
                message: "Invoice deleted",
              });
              return;
            }
            if (payload) {
              setReportData((prev) => ({
                ...prev,
                invoices: prev.invoices.map((inv) =>
                  inv.id === payload.id ? payload : inv,
                ),
              }));
              setResponseModal({
                status: "Success",
                message: "Invoice updated",
              });
            }
          }}
        />
      )}
      {selectedWorkOrder && (
        <ActionConfirm
          title={"Delete item?"}
          message={"Are you sure you want to delete this item?"}
          close={() => setSelectedWorkOrder(false)}
          confirm={deleteWorkOrder}
          requestLoading={isDeletingWorkOrder}
        />
      )}
      <div className="margin-bottom-24">
        <Button
          variant="secondary"
          onClick={() => window.history.back()}
          labelKey="back"
        />
      </div>
      <div
        className="overview-header"
        style={{ display: "flex", justifyContent: "space-between" }}
      >
        <h2 className="global-h2 black">
          {t("page-headers.maintenance-report")} #{reportData?.order_number}
        </h2>
        <div className="flex-1">
          {reportData?.status !== "completed" &&
            reportData?.status !== "closed" && (
              <button className="all-btn success-btn" onClick={markDispatch}>
                {t("buttons.mark-complete")}
              </button>
            )}
          {reportData?.status === "closed" ? (
            <button
              className="purple-btn"
              onClick={() =>
                setCompleteModal({ display: true, complete: false })
              }
            >
              {t("buttons.open")}
            </button>
          ) : (
            <button
              className="purple-btn all-btn"
              onClick={() =>
                setCompleteModal({ display: true, complete: true })
              }
            >
              {t("buttons.mark-closed")}
            </button>
          )}
          {!isComplete && (
            <button
              className="error-btn all-btn"
              onClick={() => setIsDeleting(true)}
            >
              {t("buttons.delete")}
            </button>
          )}
        </div>
      </div>
      {isLoading && <SpinnerOverlay />}
      {newTask && (
        <NewMaintenanceTask
          maintenance_report_id={reportData.id}
          close={() => setNewTask(false)}
          setData={setReportData}
        />
      )}
      {newPart && (
        <NewPartModal
          setData={setReportData}
          report={reportData}
          close={() => setNewPart(false)}
        />
      )}
      {selectedTask && (
        <EditMaintenanceTask
          data={selectedTask}
          close={() => setSelectedTask(false)}
          setData={setReportData}
        />
      )}
      {isEditing && (
        <EditMaintenanceReport
          data={reportData}
          setData={setReportData}
          close={() => setIsEditing(false)}
        />
      )}
      {selectedPart && (
        <ActionConfirm
          title={t("forms.type.maintenance-report.delete-part")}
          message={t("forms.type.maintenance-report.part-warning")}
          confirm={() => handleDeletePart()}
          close={() => setSelectedPart(null)}
          requestLoading={isLoading}
        />
      )}
      {workOrderModal && (
        <NewDoc
          close={() => setWorkOrderModal(false)}
          maintenanceReportId={reportData.id}
          onCreate={(newDocs) =>
            setReportData((prev) => ({
              ...prev,
              files: [...prev.files, ...newDocs],
            }))
          }
        />
      )}
      {reportData && (
        <>
          <div className="report-banner">
            <div className="report-header">
              <h2 className="global-h2 gray">
                {reportData.trailer
                  ? reportData.trailer.unit
                  : `${reportData.truck.company}-${reportData.truck.name}`}{" "}
              </h2>
              <h2 className="global-h2 gray">
                {convertToPST(reportData.created_at)}
              </h2>
              {reportData.status !== "closed" && (
                <button
                  className="other-btn all-btn"
                  onClick={() => setIsEditing(true)}
                >
                  {t("buttons.edit")}
                </button>
              )}
            </div>
          </div>

          <div className="report-cont-flex">
            <div className="report-cont-sm">
              <h4 className="global-h4 gray">
                {t("forms.type.maintenance-report.labels.summary")}
              </h4>
              <div className="report-row">
                <label className="input-label">
                  {t("forms.type.maintenance-report.labels.created-by")}
                </label>
                <label className="input-label">
                  {reportData.user
                    ? `${reportData.user.first_name} ${reportData.user.last_name}`
                    : `${reportData.employee.first_name} ${reportData.employee.last_name}`}
                </label>
              </div>
              <div className="report-row">
                <label className="input-label">
                  {t("yard-tables.table-columns.location")}
                </label>
                <label className="input-label">
                  {reportData.location.loc_name}
                </label>
              </div>
              <div className="report-row">
                <label className="input-label">{t("general.spot")}</label>
                <label className="input-label">
                  {reportData.assigned_spot}
                </label>
              </div>
              <div className="report-row">
                <label className="input-label">
                  {t("yard-tables.table-columns.priority")}
                </label>
                <label className="input-label">
                  <GetVehicleCondition condition={reportData.priority} />
                </label>
              </div>
              <div className="report-row">
                <label className="input-label">
                  {t("yard-tables.table-columns.status")}
                </label>
                <label className="input-label">
                  <GetVehicleCondition condition={reportData.status} />
                </label>
              </div>
              {reportData?.truck && (
                <div className="report-row">
                  <label className="input-label">
                    {t("forms.type.vehicle.labels.odometer")}
                  </label>
                  <label className="input-label">{reportData.odometer}</label>
                </div>
              )}
              <div className="report-row">
                <label className="input-label">
                  {t("forms.type.maintenance-report.labels.due-date")}
                </label>
                <label className="input-label">
                  {reportData.expected_date
                    ? convertToPST(reportData.expected_date)
                    : ""}
                </label>
              </div>
              <div className="report-row">
                <label className="input-label">
                  {t("forms.type.maintenance-report.labels.completed-date")}
                </label>
                <label className="input-label">
                  {reportData.completed_at
                    ? convertToPST(reportData.completed_at)
                    : ""}
                </label>
              </div>
            </div>
            <div className="report-cont-sm">
              <h4 className="global-h4 gray">
                {t("forms.type.vehicle.input-headers.notes")}
              </h4>
              <div
                className="report-row"
                style={{ height: "100%", textAlign: "left" }}
              >
                {reportData.notes}
              </div>
            </div>
          </div>

          {/* Tasks table */}
          {reportData.tasks.length > 0 && (
            <>
              <h4 className="global-h4 gray">
                {t("forms.type.maintenance-report.task")}
              </h4>
              <div className="table-cont">
                <table
                  id="forwardTable"
                  className="yard-table margin-bottom-24"
                >
                  <thead>
                    <tr className="table-header">
                      <th scope="col">
                        {t("forms.type.maintenance-report.labels.description")}
                      </th>
                      <th scope="col">
                        {t("forms.type.visitor.purpose.mechanic")}
                      </th>
                      <th scope="col">
                        {t("yard-tables.table-columns.status")}
                      </th>
                      <th scope="col">
                        {t("forms.type.maintenance-report.labels.created-by")}
                      </th>
                      <th scope="col">
                        {t("forms.type.maintenance-report.labels.created-at")}
                      </th>
                      <th scope="col">
                        {t(
                          "forms.type.maintenance-report.labels.completed-date",
                        )}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.tasks.map((task, index) => (
                      <tr
                        key={`task-${index}`}
                        className={`data-rows ${
                          !isComplete ? "row-hover" : ""
                        }`}
                        onClick={
                          isComplete ? null : () => setSelectedTask(task)
                        }
                        style={{
                          maxHeight: isMobile ? "fit-content" : "180px",
                        }}
                      >
                        <TableCell
                          index={index}
                          column={"Description"}
                          value={task.description}
                          isMobile={isMobile}
                        />
                        <TableCell
                          index={index}
                          column={"Mechanic"}
                          value={
                            task.assigned_mechanic_detail
                              ? task.assigned_mechanic_detail.full_name
                              : "N/A"
                          }
                          isMobile={isMobile}
                        />
                        <TableCell
                          index={index}
                          column={"Status"}
                          value={React.cloneElement(
                            <GetVehicleCondition condition={task.status} />,
                          )}
                          isMobile={isMobile}
                        />
                        <TableCell
                          index={index}
                          column={"Created by"}
                          value={`${task.created_by.first_name} ${task.created_by.last_name}`}
                          isMobile={isMobile}
                        />
                        <TableCell
                          index={index}
                          column={"Created at"}
                          value={convertToPST(task.created_at)}
                          isMobile={isMobile}
                        />
                        <TableCell
                          index={index}
                          column={"Completed at"}
                          value={convertToPST(task.completed_at)}
                          isMobile={isMobile}
                        />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {reportData.status !== "closed" && (
            <div style={{ marginBottom: "24px" }} className="flex-1">
              <button
                className="change-pwd-btn"
                onClick={() => setNewTask(true)}
              >
                {t("buttons.add-task")}
              </button>
            </div>
          )}

          {/* Used parts table */}
          {reportData.used_parts.length > 0 && (
            <>
              <h4 className="global-h4 gray">
                {t("forms.type.maintenance-report.parts-used")}
              </h4>
              <div className="table-cont">
                <table className="yard-table margin-bottom-24">
                  <thead>
                    <tr className="table-header">
                      <th scope="col">{t("yard-tables.table-columns.code")}</th>
                      <th scope="col">{t("driversTable.columns.name")}</th>
                      <th scope="col">
                        {t("forms.type.maintenance-report.labels.quantity")}
                      </th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.used_parts.map((part, index) => (
                      <tr key={index} className="data-rows">
                        <TableCell
                          index={index}
                          column={"Code"}
                          value={part.part.sku}
                          isMobile={isMobile}
                        />
                        <TableCell
                          index={index}
                          column={"Name"}
                          value={part.part.name}
                          isMobile={isMobile}
                        />
                        <TableCell
                          index={index}
                          column={"Quantity"}
                          value={part.quantity}
                          isMobile={isMobile}
                        />
                        {/* {!isComplete && ( */}
                        <td>
                          <div onClick={() => setSelectedPart(part)}>
                            <CheckOutIcon color={"#FF6363"} />
                          </div>
                        </td>
                        {/* )} */}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {/* {reportData.status !== "closed" && ( */}
          <div className="margin-bottom-24 flex-1">
            <button className="change-pwd-btn" onClick={() => setNewPart(true)}>
              {t("buttons.add-part")}
            </button>
          </div>
          {/* )} */}

          {/* Invoices table */}
          {reportData.invoices && reportData.invoices.length > 0 && (
            <>
              <h4 className="global-h4 gray">
                {t("forms.type.invoice.header") || "Invoices"}
              </h4>
              <div className="table-cont">
                <table className="yard-table margin-bottom-24">
                  <thead>
                    <tr className="table-header">
                      <th scope="col">
                        {t("forms.type.invoice.labels.invoice-number") ||
                          "Invoice #"}
                      </th>
                      <th scope="col">
                        {t("forms.type.invoice.labels.purchase-order") ||
                          "PO #"}
                      </th>
                      <th scope="col">
                        {t("forms.type.maintenance-report.labels.created-by")}
                      </th>
                      <th scope="col">
                        {t("forms.type.maintenance-report.labels.created-at")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.invoices.map((inv, index) => (
                      <tr
                        key={index}
                        className={`data-rows ${
                          reportData.status !== "closed" ? "row-hover" : ""
                        }`}
                        onClick={() => openInvoice(inv)}
                        style={{
                          maxHeight: isMobile ? "fit-content" : "180px",
                        }}
                      >
                        <TableCell
                          index={index}
                          column={"Invoice #"}
                          value={inv.invoice_number}
                          isMobile={isMobile}
                        />
                        <TableCell
                          index={index}
                          column={"PO #"}
                          value={inv.purchase_order || ""}
                          isMobile={isMobile}
                        />
                        <TableCell
                          index={index}
                          column={"Created by"}
                          value={
                            inv.created_by
                              ? `${inv.created_by.first_name} ${inv.created_by.last_name}`
                              : ""
                          }
                          isMobile={isMobile}
                        />
                        <TableCell
                          index={index}
                          column={"Created at"}
                          value={convertToPST(inv.created_at)}
                          isMobile={isMobile}
                        />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Work orders table */}
          {reportData.files && reportData.files.length > 0 && (
            <>
              <h4 className="global-h4 gray">{t("general.files")}</h4>
              <div className="table-cont">
                <table className="yard-table margin-bottom-24">
                  <thead>
                    <tr className="table-header">
                      <th scope="col">{t("yard-tables.table-columns.name")}</th>
                      <th scope="col">
                        {t("yard-tables.table-columns.fileType")}
                      </th>
                      <th scope="col">
                        {t("forms.type.maintenance-report.labels.created-by")}
                      </th>
                      <th scope="col"></th>
                      {reportData.status !== "closed" && <th scope="col"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.files.map((file, index) => (
                      <tr key={`file-${index}`} className="data-rows">
                        <TableCell
                          index={index}
                          column={"Name"}
                          value={sliceName(file.blob_name)}
                          isMobile={isMobile}
                        />
                        <TableCell
                          index={index}
                          column={"File type"}
                          value={sliceName(file.content_type)}
                          isMobile={isMobile}
                        />
                        <TableCell
                          index={index}
                          column={"Created by"}
                          value={
                            file.uploaded_by ? file.uploaded_by.full_name : ""
                          }
                          isMobile={isMobile}
                        />
                        <div onClick={() => downloadFile(file)}>
                          <TableCell
                            index={index}
                            column={"Download"}
                            value={React.cloneElement(
                              <FontAwesomeIcon
                                icon={faDownload}
                                style={{
                                  color: "#2563eb",
                                }}
                              />,
                            )}
                          />
                        </div>
                        {reportData.status !== "closed" && (
                          <TableCell
                            index={index}
                            column={"Delete"}
                            value={React.cloneElement(
                              <TrashIconCont
                                onClick={() => setSelectedWorkOrder(file)}
                              />,
                            )}
                            isMobile={isMobile}
                          />
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <div className="margin-bottom-24 flex-1">
            <button
              className="change-pwd-btn"
              onClick={() => setWorkOrderModal(true)}
            >
              {t("buttons.addOrder")}
            </button>
          </div>

          {!newInvoiceOpen && (
            <div className="margin-bottom-24 flex-1">
              <button
                className="change-pwd-btn"
                onClick={() => setNewInvoiceOpen(true)}
              >
                {t("buttons.add-invoice") || "Add Invoice"}
              </button>
            </div>
          )}
        </>
      )}
    </PageLayout>
  );
};

export default MaintenanceReportView;
